import React from 'react';
import { osc, orbitAngle, wobble } from '../../core/AnimationUtils';

/* ━━━ 1. Cell Diagram ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface CellDiagramProps {
  highlightTarget?: string;
  labels?: Record<string, string>;
  accentColor?: string;
  opacity?: number;
  /** Seconds since the scene started — drives organelle drift + cytoplasm flow. */
  t?: number;
}
export const CellDiagram: React.FC<CellDiagramProps> = ({
  highlightTarget, labels = { membrane: 'Membrane', nucleus: 'Nucleus', mito: 'Mitochondria', er: 'ER' },
  accentColor = '#22c55e', opacity = 1, t = 0,
}) => {
  const cx = 400, cy = 260;
  const organelles = [
    { id: 'nucleus', x: cx - 10, y: cy - 5, rx: 55, ry: 50, label: labels.nucleus || 'Nucleus' },
    { id: 'mito', x: cx + 110, y: cy - 50, rx: 30, ry: 18, label: labels.mito || 'Mito' },
    { id: 'mito2', x: cx - 100, y: cy + 60, rx: 28, ry: 16, label: '' },
    { id: 'er', x: cx + 60, y: cy + 70, rx: 45, ry: 14, label: labels.er || 'ER' },
  ];
  return (
    <g opacity={opacity}>
      <defs>
        <radialGradient id="cd-mem" cx="40%" cy="35%"><stop offset="0%" stopColor="#1e3a5f"/><stop offset="80%" stopColor="#0f172a"/><stop offset="100%" stopColor={accentColor}/></radialGradient>
        <radialGradient id="cd-nuc" cx="35%" cy="30%"><stop offset="0%" stopColor="#fbbf24"/><stop offset="100%" stopColor="#92400e"/></radialGradient>
        <radialGradient id="cd-org" cx="35%" cy="30%"><stop offset="0%" stopColor="#86efac"/><stop offset="100%" stopColor="#166534"/></radialGradient>
        <filter id="cd-sh"><feDropShadow dx="3" dy="4" stdDeviation="5" floodOpacity="0.35"/></filter>
      </defs>
      {/* Cell membrane shadow */}
      <ellipse cx={cx+4} cy={cy+5} rx="185" ry="145" fill="rgba(0,0,0,0.3)"/>
      {/* Cell membrane */}
      <ellipse cx={cx} cy={cy} rx="185" ry="145" fill="url(#cd-mem)" stroke={highlightTarget==='membrane'?'#fff':accentColor} strokeWidth={highlightTarget==='membrane'?3:2} filter="url(#cd-sh)"/>
      {/* Cytoplasm dots (slow swirl around the cell) */}
      {Array.from({length: 20}, (_, i) => {
        const a = i * 0.31 + 0.5 + t * 0.12, rr = 60 + (i % 7) * 15;
        return <circle key={`d${i}`} cx={cx + rr * Math.cos(a)} cy={cy + rr * Math.sin(a) * 0.78} r="2" fill={accentColor} opacity="0.15"/>;
      })}
      {/* Organelles (gentle independent drift, like floating in cytoplasm) */}
      {organelles.map((o, i) => {
        const hi = highlightTarget === o.id;
        const isNuc = o.id === 'nucleus';
        // The nucleus barely moves; smaller organelles drift more.
        const amp = isNuc ? 1.5 : 4;
        const ox = o.x + wobble(t, 4.5 + i * 0.7, amp, i);
        const oy = o.y + wobble(t, 5.3 + i * 0.6, amp * 0.8, i * 1.4);
        return <g key={o.id}>
          <ellipse cx={ox+2} cy={oy+3} rx={o.rx} ry={o.ry} fill="rgba(0,0,0,0.25)"/>
          <ellipse cx={ox} cy={oy} rx={o.rx} ry={o.ry} fill={isNuc ? 'url(#cd-nuc)' : 'url(#cd-org)'} stroke={hi?'#fff':'rgba(255,255,255,0.2)'} strokeWidth={hi?2.5:1}/>
          <ellipse cx={ox - o.rx*0.25} cy={oy - o.ry*0.3} rx={o.rx*0.3} ry={o.ry*0.25} fill="rgba(255,255,255,0.15)"/>
          {o.label && <text x={ox} y={oy + o.ry + 16} textAnchor="middle" fill="#e2e8f0" fontSize="10" fontFamily="Inter,sans-serif" fontWeight="500">{o.label}</text>}
          {hi && <ellipse cx={ox} cy={oy} rx={o.rx+8} ry={o.ry+6} fill="none" stroke="#fff" strokeWidth="2" opacity="0.5"><animate attributeName="opacity" from="0.5" to="0" dur="1s" repeatCount="indefinite"/></ellipse>}
        </g>;
      })}
      <text x={cx} y={cy + 160} textAnchor="middle" fill="#94a3b8" fontSize="10" fontFamily="Inter,sans-serif">{labels.membrane || 'Cell Membrane'}</text>
    </g>
  );
};

/* ━━━ 2. Atom Orbital ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface AtomOrbitalProps {
  highlightTarget?: string;
  labels?: Record<string, string>;
  accentColor?: string;
  opacity?: number;
  electronCount?: number;
  /** Seconds since the scene started — drives orbiting electrons. */
  t?: number;
}
export const AtomOrbital: React.FC<AtomOrbitalProps> = ({
  highlightTarget, labels = { nucleus: 'Nucleus', orbit1: 'Shell 1', orbit2: 'Shell 2' },
  accentColor = '#f59e0b', opacity = 1, electronCount = 6, t = 0,
}) => {
  const cx = 400, cy = 260;
  const orbits = [{ rx: 90, ry: 50, tilt: -20 }, { rx: 140, ry: 70, tilt: 25 }, { rx: 180, ry: 55, tilt: -45 }];
  // Inner shells orbit faster (seconds per revolution); subtle nucleus breathing.
  const shellPeriod = [3.0, 4.6, 6.2];
  const nucleusR = 30 + osc(t, 2.4, -1.2, 1.2);
  return (
    <g opacity={opacity}>
      <defs>
        <radialGradient id="ao-nuc" cx="35%" cy="30%"><stop offset="0%" stopColor="#fef3c7"/><stop offset="60%" stopColor={accentColor}/><stop offset="100%" stopColor="#92400e"/></radialGradient>
        <radialGradient id="ao-el" cx="30%" cy="25%"><stop offset="0%" stopColor="#e2e8f0"/><stop offset="100%" stopColor="#3b82f6"/></radialGradient>
        <filter id="ao-sh"><feDropShadow dx="2" dy="3" stdDeviation="3" floodOpacity="0.35"/></filter>
      </defs>
      {/* Orbits */}
      {orbits.map((o, i) => {
        const id = `orbit_${i+1}`;
        const hi = highlightTarget === id;
        return <g key={i} transform={`rotate(${o.tilt},${cx},${cy})`}>
          <ellipse cx={cx} cy={cy} rx={o.rx} ry={o.ry} fill="none" stroke={hi?'#fff':accentColor} strokeWidth={hi?2:1} opacity={hi?0.8:0.3} strokeDasharray={hi?'':'6,4'}/>
        </g>;
      })}
      {/* Electrons */}
      {Array.from({length: electronCount}, (_, i) => {
        const oi = i % orbits.length;
        const o = orbits[oi];
        // Electrons orbit continuously; per-electron phase keeps them spread out.
        const angle = orbitAngle(t, shellPeriod[oi], (2 * Math.PI * i) / electronCount + i * 0.5);
        const tiltRad = (o.tilt * Math.PI) / 180;
        const ex = cx + o.rx * Math.cos(angle) * Math.cos(tiltRad) - o.ry * Math.sin(angle) * Math.sin(tiltRad);
        const ey = cy + o.rx * Math.cos(angle) * Math.sin(tiltRad) + o.ry * Math.sin(angle) * Math.cos(tiltRad);
        return <g key={`e${i}`}>
          <circle cx={ex+1} cy={ey+2} r="7" fill="rgba(0,0,0,0.3)"/>
          <circle cx={ex} cy={ey} r="7" fill="url(#ao-el)"/>
          <circle cx={ex-2} cy={ey-3} r="2" fill="rgba(255,255,255,0.5)"/>
        </g>;
      })}
      {/* Nucleus (gentle breathing) */}
      <circle cx={cx+3} cy={cy+4} r={nucleusR} fill="rgba(0,0,0,0.3)"/>
      <circle cx={cx} cy={cy} r={nucleusR} fill="url(#ao-nuc)" stroke={highlightTarget==='nucleus'?'#fff':'rgba(255,255,255,0.2)'} strokeWidth={highlightTarget==='nucleus'?3:1} filter="url(#ao-sh)"/>
      <circle cx={cx-9} cy={cy-10} r="6" fill="rgba(255,255,255,0.35)"/>
      <text x={cx} y={cy + 48} textAnchor="middle" fill="#e2e8f0" fontSize="10" fontFamily="Inter,sans-serif">{labels.nucleus||'Nucleus'}</text>
    </g>
  );
};

/* ━━━ 3. DNA Helix ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface DNAHelixProps {
  highlightTarget?: string;
  labels?: string[];
  accentColor?: string;
  opacity?: number;
  /** Seconds since the scene started — drives helix rotation twist. */
  t?: number;
}
export const DNAHelix: React.FC<DNAHelixProps> = ({
  highlightTarget, labels = ['Adenine','Thymine','Guanine','Cytosine'],
  accentColor = '#a855f7', opacity = 1, t = 0,
}) => {
  const cx = 400, startY = 40, endY = 460, steps = 24;
  const amp = 80, freq = 0.35;
  // Rotate the helix by advancing the phase with t — one full twist every 4 s.
  const twist = (t / 4) * Math.PI * 2;
  const pts1: {x:number;y:number}[] = [], pts2: {x:number;y:number}[] = [];
  for (let i = 0; i <= steps; i++) {
    const frac = i / steps;
    const y = startY + frac * (endY - startY);
    pts1.push({ x: cx + amp * Math.sin(freq * y + twist), y });
    pts2.push({ x: cx - amp * Math.sin(freq * y + twist), y });
  }
  const path1 = pts1.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const path2 = pts2.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="dna-s1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={accentColor}/><stop offset="100%" stopColor="#6d28d9"/></linearGradient>
        <linearGradient id="dna-s2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#ec4899"/><stop offset="100%" stopColor="#be185d"/></linearGradient>
        <filter id="dna-sh"><feDropShadow dx="2" dy="2" stdDeviation="2" floodOpacity="0.3"/></filter>
      </defs>
      {/* Shadow strands */}
      <path d={path1} fill="none" stroke="rgba(0,0,0,0.3)" strokeWidth="6" transform="translate(2,3)"/>
      <path d={path2} fill="none" stroke="rgba(0,0,0,0.3)" strokeWidth="6" transform="translate(2,3)"/>
      {/* Strands */}
      <path d={path1} fill="none" stroke="url(#dna-s1)" strokeWidth="5" strokeLinecap="round"/>
      <path d={path2} fill="none" stroke="url(#dna-s2)" strokeWidth="5" strokeLinecap="round"/>
      {/* Specular */}
      <path d={path1} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5"/>
      <path d={path2} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5"/>
      {/* Rungs (base pairs) — travel with the twist */}
      {Array.from({length: 8}, (_, i) => {
        const idx = Math.floor((i + 1) * steps / 9);
        const p1 = pts1[idx], p2 = pts2[idx];
        if (!p1 || !p2) return null;
        const hi = highlightTarget === `rung_${i+1}`;
        // Rung brightness pulses in a staggered wave to suggest base-pair vibration.
        const rungOp = 0.2 + 0.3 * ((Math.sin(twist * 2 + i * 0.8) + 1) / 2);
        return <g key={`r${i}`}>
          <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke={hi?'#fff':'rgba(255,255,255,0.25)'} strokeWidth={hi?3:2} opacity={hi ? 1 : rungOp + 0.15}/>
          {labels[i % labels.length] && i < 4 && <text x={(p1.x+p2.x)/2} y={(p1.y+p2.y)/2 - 8} textAnchor="middle" fill="#e2e8f0" fontSize="9" fontFamily="Inter,sans-serif">{labels[i % labels.length]}</text>}
        </g>;
      })}
    </g>
  );
};

/* ━━━ 4. Layered Cross-Section ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface LayeredCrossProps {
  highlightTarget?: string;
  labels?: string[];
  accentColor?: string;
  opacity?: number;
  /** Seconds since the scene started — drives inner-core pulse and mantle convection shimmer. */
  t?: number;
}
export const LayeredCross: React.FC<LayeredCrossProps> = ({
  highlightTarget, labels = ['Crust','Mantle','Outer Core','Inner Core'],
  accentColor = '#ef4444', opacity = 1, t = 0,
}) => {
  const cx = 400, cy = 280, maxR = 175, n = labels.length;
  // Inner core breathes (slow, powerful); each layer has a subtler independent pulse.
  const layerPulse = [
    osc(t, 6.0, -1.5, 1.5),  // crust: very subtle seismic shimmer
    osc(t, 4.5, -2.0, 2.0),  // mantle: slow convection swell
    osc(t, 3.0, -2.5, 2.5),  // outer core: fluid motion
    osc(t, 2.0, -3.0, 3.0),  // inner core: rapid deep pulse
  ];
  return (
    <g opacity={opacity}>
      <defs><filter id="lc-sh"><feDropShadow dx="3" dy="4" stdDeviation="4" floodOpacity="0.35"/></filter></defs>
      {/* Draw from outer to inner */}
      {[...labels].reverse().map((label, ri) => {
        const i = n - 1 - ri;
        const r = maxR * ((i + 1) / n) + layerPulse[i];
        const hue = i * 25;
        const hi = highlightTarget === `layer_${i+1}`;
        // Half-circle cross section
        return <g key={i} filter="url(#lc-sh)">
          <path d={`M${cx-r},${cy} A${r},${r} 0 0,1 ${cx+r},${cy} L${cx-r},${cy}Z`}
            fill={accentColor} opacity={0.35 + i * 0.15} stroke={hi?'#fff':'rgba(255,255,255,0.1)'} strokeWidth={hi?2.5:1}
            style={{ filter: `hue-rotate(${hue}deg)` }}/>
          <text x={cx} y={cy - r + 20 + i * 6} textAnchor="middle" fill="#e2e8f0" fontSize="10" fontFamily="Inter,sans-serif" fontWeight="500">{label}</text>
          {hi && <path d={`M${cx-r-4},${cy} A${r+4},${r+4} 0 0,1 ${cx+r+4},${cy}`} fill="none" stroke="#fff" strokeWidth="2" opacity="0.5"><animate attributeName="opacity" from="0.5" to="0" dur="1s" repeatCount="indefinite"/></path>}
        </g>;
      })}
      {/* Ground line */}
      <line x1={cx-maxR-20} y1={cy} x2={cx+maxR+20} y2={cy} stroke="#475569" strokeWidth="2"/>
    </g>
  );
};

/* ━━━ 5. Wave Pattern ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface WavePatternProps {
  highlightTarget?: string;
  labels?: Record<string, string>;
  accentColor?: string;
  opacity?: number;
  frequency?: number;
  /** Seconds since the scene started — drives wave propagation. */
  t?: number;
}
export const WavePattern: React.FC<WavePatternProps> = ({
  highlightTarget, labels = { peak: 'Peak', trough: 'Trough', wavelength: 'Wavelength' },
  accentColor = '#0ea5e9', opacity = 1, frequency = 2, t = 0,
}) => {
  const startX = 80, endX = 720, cy = 260, amp = 100;
  const steps = 100;
  // Phase advances with t so the wave propagates rightward; one full cycle every 3 s.
  const phase = (t / 3) * Math.PI * 2;
  const pts = Array.from({length: steps+1}, (_, i) => {
    const frac = i / steps;
    const x = startX + frac * (endX - startX);
    const y = cy - amp * Math.sin(frac * Math.PI * 2 * frequency - phase);
    return { x, y };
  });
  const pathD = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const peakHi = highlightTarget === 'peak';
  const troughHi = highlightTarget === 'trough';
  // Peak and trough markers travel with the wave.
  const peakX = startX + (endX - startX) / (4 * frequency) + (phase / (Math.PI * 2)) * (endX - startX) / frequency;
  const troughX = startX + 3 * (endX - startX) / (4 * frequency) + (phase / (Math.PI * 2)) * (endX - startX) / frequency;
  // Clamp markers into view.
  const clamp = (v: number) => Math.max(startX + 20, Math.min(endX - 20, ((v - startX) % (endX - startX) + (endX - startX)) % (endX - startX) + startX));
  const peakXc = clamp(peakX);
  const troughXc = clamp(troughX);
  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="wp-wave" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor={accentColor}/><stop offset="100%" stopColor="#6366f1"/></linearGradient>
        <filter id="wp-sh"><feDropShadow dx="1" dy="2" stdDeviation="2" floodOpacity="0.3"/></filter>
      </defs>
      {/* Center axis */}
      <line x1={startX} y1={cy} x2={endX} y2={cy} stroke="#475569" strokeWidth="1" strokeDasharray="4,4"/>
      {/* Wave shadow */}
      <path d={pathD} fill="none" stroke="rgba(0,0,0,0.3)" strokeWidth="5" transform="translate(2,3)"/>
      {/* Wave */}
      <path d={pathD} fill="none" stroke="url(#wp-wave)" strokeWidth="4" strokeLinecap="round" filter="url(#wp-sh)"/>
      <path d={pathD} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5"/>
      {/* Peak marker — travels with the propagating wave */}
      <circle cx={peakXc} cy={cy - amp} r="8" fill={peakHi?'#fff':accentColor} opacity={peakHi?1:0.7}/>
      <text x={peakXc} y={cy - amp - 14} textAnchor="middle" fill="#e2e8f0" fontSize="10" fontFamily="Inter,sans-serif">{labels.peak||'Peak'}</text>
      {/* Trough marker — travels with the propagating wave */}
      <circle cx={troughXc} cy={cy + amp} r="8" fill={troughHi?'#fff':accentColor} opacity={troughHi?1:0.7}/>
      <text x={troughXc} y={cy + amp + 20} textAnchor="middle" fill="#e2e8f0" fontSize="10" fontFamily="Inter,sans-serif">{labels.trough||'Trough'}</text>
      {/* Wavelength annotation */}
      <line x1={startX} y1={cy+amp+40} x2={startX+(endX-startX)/frequency} y2={cy+amp+40} stroke={accentColor} strokeWidth="1.5" markerEnd="url(#wp-arr)" markerStart="url(#wp-arr)"/>
      <text x={startX + (endX-startX)/(2*frequency)} y={cy+amp+56} textAnchor="middle" fill="#94a3b8" fontSize="10" fontFamily="Inter,sans-serif">{labels.wavelength||'λ'}</text>
    </g>
  );
};

/* ━━━ 6. Heart Chambers ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface HeartChambersProps {
  highlightTarget?: string;
  labels?: Record<string, string>;
  accentColor?: string;
  opacity?: number;
  /** Seconds since the scene started — drives heartbeat pulse (systole/diastole). */
  t?: number;
}
export const HeartChambers: React.FC<HeartChambersProps> = ({
  highlightTarget, labels = { la: 'Left Atrium', ra: 'Right Atrium', lv: 'Left Ventricle', rv: 'Right Ventricle' },
  accentColor = '#ef4444', opacity = 1, t = 0,
}) => {
  const cx = 400, cy = 240;
  // Heartbeat: ~72 bpm ≈ one beat per 0.83 s. Use a 0.8 s period.
  // osc gives a smooth sine; we want a sharper systole. We compute the raw sine
  // and bias it so the "contraction" peak is brief.
  const beatPhase = ((t % 0.8) / 0.8) * Math.PI * 2;
  // Scale factor: 1.0 at rest, briefly swells to 1.06 at systole.
  const beat = 1 + 0.06 * Math.max(0, Math.sin(beatPhase));
  // Ventricles contract more than atria; right/left are offset by a tiny phase.
  const lvBeat = 1 + 0.09 * Math.max(0, Math.sin(beatPhase));
  const rvBeat = 1 + 0.09 * Math.max(0, Math.sin(beatPhase - 0.15));
  // Scale the whole heart shape around its centre.
  return (
    <g opacity={opacity} transform={`scale(${beat})`} style={{ transformOrigin: `${cx}px ${cy}px`, transformBox: 'fill-box' }}>
      <defs>
        <radialGradient id="hc-bg" cx="40%" cy="35%"><stop offset="0%" stopColor="#fca5a5"/><stop offset="100%" stopColor="#7f1d1d"/></radialGradient>
        <filter id="hc-sh"><feDropShadow dx="3" dy="4" stdDeviation="5" floodOpacity="0.4"/></filter>
      </defs>
      {/* Heart shape shadow */}
      <path d={`M${cx+3},${cy+170} C${cx-130},${cy+80} ${cx-180},${cy-40} ${cx-92},${cy-100} C${cx-40},${cy-140} ${cx+3},${cy-80} ${cx+3},${cy-40} C${cx+3},${cy-80} ${cx+46},${cy-140} ${cx+98},${cy-100} C${cx+186},${cy-40} ${cx+136},${cy+80} ${cx+3},${cy+170}Z`}
        fill="rgba(0,0,0,0.3)" transform="translate(4,5)"/>
      {/* Heart shape */}
      <path d={`M${cx},${cy+165} C${cx-135},${cy+75} ${cx-185},${cy-45} ${cx-95},${cy-105} C${cx-43},${cy-145} ${cx},${cy-85} ${cx},${cy-45} C${cx},${cy-85} ${cx+43},${cy-145} ${cx+95},${cy-105} C${cx+185},${cy-45} ${cx+135},${cy+75} ${cx},${cy+165}Z`}
        fill="url(#hc-bg)" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" filter="url(#hc-sh)"/>
      {/* Septum lines */}
      <line x1={cx} y1={cy-40} x2={cx} y2={cy+130} stroke="rgba(255,255,255,0.2)" strokeWidth="2"/>
      <line x1={cx-100} y1={cy+30} x2={cx+100} y2={cy+30} stroke="rgba(255,255,255,0.15)" strokeWidth="1.5"/>
      {/* Chamber contraction glow — ventricles squeeze inward on systole */}
      <ellipse cx={cx+50} cy={cy+60} rx={55 * (2 - lvBeat)} ry={45 * (2 - lvBeat)} fill={accentColor} opacity={0.12 * (lvBeat - 1) * 10}/>
      <ellipse cx={cx-50} cy={cy+60} rx={55 * (2 - rvBeat)} ry={45 * (2 - rvBeat)} fill={accentColor} opacity={0.12 * (rvBeat - 1) * 10}/>
      {/* Chamber labels */}
      {[
        { id: 'la', x: cx+50, y: cy-15, label: labels.la },
        { id: 'ra', x: cx-50, y: cy-15, label: labels.ra },
        { id: 'lv', x: cx+50, y: cy+70, label: labels.lv },
        { id: 'rv', x: cx-50, y: cy+70, label: labels.rv },
      ].map(ch => {
        const hi = highlightTarget === ch.id;
        return <g key={ch.id}>
          {hi && <circle cx={ch.x} cy={ch.y} r="32" fill={accentColor} opacity="0.3"><animate attributeName="opacity" from="0.3" to="0.05" dur="1s" repeatCount="indefinite"/></circle>}
          <text x={ch.x} y={ch.y+4} textAnchor="middle" fill={hi?'#fff':'#fecaca'} fontSize="10" fontFamily="Inter,sans-serif" fontWeight={hi?'700':'500'}>{ch.label}</text>
        </g>;
      })}
    </g>
  );
};

/* ━━━ 7. Solar System ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface SolarSystemProps {
  highlightTarget?: string;
  labels?: string[];
  accentColor?: string;
  opacity?: number;
  /** Seconds since the scene started — drives orbiting planets. */
  t?: number;
}
export const SolarSystem: React.FC<SolarSystemProps> = ({
  highlightTarget, labels = ['Mercury','Venus','Earth','Mars'],
  accentColor = '#f59e0b', opacity = 1, t = 0,
}) => {
  const cx = 400, cy = 280, baseR = 60;
  const sunR = 28 + osc(t, 3.5, -1, 1);
  const colors = ['#94a3b8','#fbbf24','#3b82f6','#ef4444','#a855f7','#10b981'];
  return (
    <g opacity={opacity}>
      <defs>
        <radialGradient id="ss-sun" cx="40%" cy="35%"><stop offset="0%" stopColor="#fef3c7"/><stop offset="50%" stopColor="#f59e0b"/><stop offset="100%" stopColor="#92400e"/></radialGradient>
        <filter id="ss-glow"><feGaussianBlur stdDeviation="8" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        <filter id="ss-sh"><feDropShadow dx="2" dy="2" stdDeviation="2" floodOpacity="0.3"/></filter>
      </defs>
      {/* Orbits */}
      {labels.map((_, i) => {
        const r = baseR + (i + 1) * 40;
        return <ellipse key={`o${i}`} cx={cx} cy={cy} rx={r} ry={r * 0.4} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1" strokeDasharray="4,6"/>;
      })}
      {/* Sun (gentle pulse) */}
      <circle cx={cx+2} cy={cy+3} r={sunR} fill="rgba(0,0,0,0.3)"/>
      <circle cx={cx} cy={cy} r={sunR} fill="url(#ss-sun)" filter="url(#ss-glow)"/>
      <circle cx={cx-8} cy={cy-10} r="5" fill="rgba(255,255,255,0.4)"/>
      {/* Planets (orbit continuously — inner planets faster, like real periods) */}
      {labels.map((label, i) => {
        const orbR = baseR + (i + 1) * 40;
        const angle = orbitAngle(t, 6 + i * 3.5, Math.PI * 0.3 + i * 1.1);
        const px = cx + orbR * Math.cos(angle);
        const py = cy + orbR * 0.4 * Math.sin(angle);
        const pSize = 8 + (i === 2 ? 4 : i === 3 ? 2 : 0);
        const id = `planet_${i+1}`;
        const hi = highlightTarget === id;
        const c = colors[i % colors.length];
        return <g key={i} filter="url(#ss-sh)">
          <circle cx={px+1} cy={py+2} r={pSize} fill="rgba(0,0,0,0.3)"/>
          <circle cx={px} cy={py} r={pSize} fill={c} stroke={hi?'#fff':'none'} strokeWidth="2"/>
          <circle cx={px-pSize*0.3} cy={py-pSize*0.3} r={pSize*0.25} fill="rgba(255,255,255,0.4)"/>
          <text x={px} y={py+pSize+14} textAnchor="middle" fill="#e2e8f0" fontSize="9" fontFamily="Inter,sans-serif">{label}</text>
          {hi && <circle cx={px} cy={py} r={pSize+8} fill="none" stroke="#fff" strokeWidth="1.5" opacity="0.5"><animate attributeName="opacity" from="0.5" to="0" dur="1s" repeatCount="indefinite"/></circle>}
        </g>;
      })}
    </g>
  );
};
